import React from 'react';
import {
    EyeOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FilePptOutlined,
    FileZipOutlined,
    FileTextOutlined,
    CustomerServiceOutlined,
    FileImageOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { useTaskAttachmentTile } from '@hooks/projects/task/useTaskAttachmentTile';
import {
    AttachmentCard,
    AttachmentThumbnailFileExt,
    AttachmentThumbnailFileWrap,
    AttachmentThumbnailImg,
} from '@/pages/inside/tenant/style';
import { FileAttachmentContainer } from '@/components/module/projects/task/TaskAttachmentTile.parts';

const getFileIcon = (ext) => {
    const format = String(ext || '').toLowerCase();
    switch (format) {
        case 'pdf':
            return { Icon: FilePdfOutlined, color: '#e74c3c' }; // Red
        case 'doc':
        case 'docx':
        case 'odt':
            return { Icon: FileWordOutlined, color: '#2b579a' }; // Blue
        case 'xls':
        case 'xlsx':
        case 'csv':
        case 'ods':
            return { Icon: FileExcelOutlined, color: '#217346' }; // Green
        case 'ppt':
        case 'pptx':
        case 'odp':
            return { Icon: FilePptOutlined, color: '#b7472a' }; // Orange
        case 'zip':
        case 'rar':
        case '7z':
        case 'tar':
        case 'gz':
            return { Icon: FileZipOutlined, color: '#faad14' }; // Amber/Yellow
        case 'txt':
        case 'log':
        case 'md':
        case 'json':
        case 'xml':
        case 'html':
        case 'css':
        case 'js':
            return { Icon: FileTextOutlined, color: '#8c8c8c' }; // Grey
        case 'mp3':
        case 'wav':
        case 'ogg':
        case 'm4a':
        case 'flac':
            return { Icon: CustomerServiceOutlined, color: '#722ed1' }; // Purple
        case 'png':
        case 'jpg':
        case 'jpeg':
        case 'gif':
        case 'webp':
        case 'svg':
            return { Icon: FileImageOutlined, color: '#13c2c2' }; // Teal
        default:
            return { Icon: FileOutlined, color: '#8c8c8c' }; // Default Grey
    }
};

export default function TaskAttachmentTile({ href, title, isImage, ext, onPreviewImage, onOpenFile, onRemove, style }) {
    const vm = useTaskAttachmentTile({ href, title, isImage, ext, onPreviewImage, onOpenFile });

    const resolvedExt = String(
        ext ||
        (title ? title.split('.').pop() : '') ||
        (href ? href.split('.').pop().split('?')[0] : '')
    ).toLowerCase();

    const { Icon, color } = getFileIcon(resolvedExt);

    return (
        <AttachmentCard
            role="button"
            tabIndex={0}
            title={vm.label}
            onMouseEnter={vm.onMouseEnter}
            onMouseLeave={vm.onMouseLeave}
            onClick={vm.handleOpen}
            onKeyDown={vm.onKeyDown}
            style={{
                position: 'relative',
                cursor: vm.canInteract ? 'pointer' : 'default',
                width: '120px',
                height: '120px',
                borderRadius: '12px',
                border: '1px solid var(--ant-color-border, #e8e8e8)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                flexShrink: 0,
                background: 'var(--ant-color-bg-container, #fff)',
                ...style,
            }}
        >
            {isImage && href ? (
                <>
                    <AttachmentThumbnailImg
                        src={href}
                        alt={vm.label}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                    <FileAttachmentContainer
                        visible={vm.hovered}
                        clickable={vm.canInteract}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            vm.handleOpen();
                        }}
                    >
                        <Container
                            $width="36px"
                            $height="36px"
                            $borderRadius="999px"
                            $display="flex"
                            $alignItems="center"
                            $justifyContent="center"
                            style={{
                                background: 'rgba(0,0,0,0.45)',
                                border: '1px solid rgba(255,255,255,0.22)',
                                color: '#fff',
                                transform: vm.hovered ? 'scale(1)' : 'scale(0.92)',
                                transition: 'transform 160ms ease, opacity 160ms ease',
                                opacity: vm.hovered ? 1 : 0,
                            }}
                        >
                            <EyeOutlined style={{ fontSize: 16 }} />
                        </Container>
                    </FileAttachmentContainer>
                </>
            ) : (
                <>
                    <AttachmentThumbnailFileWrap style={{ padding: '8px 12px', width: '100%', height: '100%', boxSizing: 'border-box' }}>
                        <Icon style={{ fontSize: 32, color: color, marginBottom: 8 }} />
                        <AttachmentThumbnailFileExt style={{ fontSize: 11, fontWeight: '700' }}>
                            {resolvedExt ? resolvedExt.toUpperCase() : 'FILE'}
                        </AttachmentThumbnailFileExt>
                        {title && (
                            <div
                                style={{
                                    fontSize: '10px',
                                    color: 'var(--ant-color-text-secondary, #8c8c8c)',
                                    marginTop: '4px',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                title={title}
                            >
                                {title}
                            </div>
                        )}
                    </AttachmentThumbnailFileWrap>
                    <FileAttachmentContainer
                        visible={vm.hovered}
                        clickable={vm.canInteract}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            vm.handleOpen();
                        }}
                    >
                        <Container
                            $width="36px"
                            $height="36px"
                            $borderRadius="999px"
                            $display="flex"
                            $alignItems="center"
                            $justifyContent="center"
                            style={{
                                background: 'rgba(0,0,0,0.45)',
                                border: '1px solid rgba(255,255,255,0.22)',
                                color: '#fff',
                                transform: vm.hovered ? 'scale(1)' : 'scale(0.92)',
                                transition: 'transform 160ms ease, opacity 160ms ease',
                                opacity: vm.hovered ? 1 : 0,
                            }}
                        >
                            <EyeOutlined style={{ fontSize: 16 }} />
                        </Container>
                    </FileAttachmentContainer>
                </>
            )}
            {onRemove && (
                <button
                    type="button"
                    aria-label="Remove attachment"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove();
                    }}
                    style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        border: 'none',
                        background: 'rgba(0, 0, 0, 0.65)',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        opacity: vm.hovered ? 1 : 0,
                        pointerEvents: vm.hovered ? 'auto' : 'none',
                        transition: 'opacity 150ms ease, background 150ms ease',
                    }}
                >
                    <DeleteOutlined style={{ fontSize: 12 }} />
                </button>
            )}
        </AttachmentCard>
    );
}

